import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "./Bits";
import { Button } from "../ui/button";
import { TaskForm } from "./TaskForm";
import { TaskList, TaskShape } from "./TaskList";
import { EventList, EventShape } from "./EventList";
import { EventForm } from "./EventForm";
import { CalendarClock, ListChecks, AlertTriangle, CheckCircle2, Plus, CalendarPlus, CalendarRange, ClipboardCheck, BadgeDollarSign, Receipt, ArrowRight } from "lucide-react";
import { canAtLeast, useEffectiveRole } from "../../lib/roles";

type MyDayResponse = {
  due_today: TaskShape[];
  overdue_follow_ups: TaskShape[];
  upcoming: TaskShape[];
  recent_completed: TaskShape[];
  todays_appointments: EventShape[];
  tenant_timezone: string;
  server_now_utc: string;
};

type ComplaintAction = {
  id: string;
  kind: "complaint_review" | "ir_response";
  case_number?: string;
  status?: string;
  title: string;
  due_at?: string | null;
  href: string;
};

type ApprovalSummary = {
  visits: { count: number; oldest?: string };
  commissions: { count: number; amount: number; currency: string; oldest?: string };
  claims: { count: number; amount: number; currency: string; oldest?: string };
};

const emptyApprovals: ApprovalSummary = {
  visits: { count: 0 },
  commissions: { count: 0, amount: 0, currency: "CAD" },
  claims: { count: 0, amount: 0, currency: "CAD" },
};

function oldestDate(values: Array<string | undefined>): string | undefined {
  return values.filter(Boolean).sort()[0];
}

function waitingLabel(value?: string): string {
  if (!value) return "No waiting items";
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000));
  return days === 0 ? "Oldest submitted today" : `Oldest waiting ${days} day${days === 1 ? "" : "s"}`;
}

const money = (amount: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount || 0);

const Section: React.FC<{
  title: string; icon: React.ReactNode; testId: string;
  children: React.ReactNode;
}> = ({ title, icon, testId, children }) => (
  <section className="border border-slate-200 rounded-lg bg-white p-4 md:p-5"
    data-testid={`myday-section-${testId}`}>
    <div className="flex items-center gap-2 text-slate-800 font-semibold" style={{ fontFamily: "Manrope" }}>
      {icon}
      <h2 className="text-base md:text-lg">{title}</h2>
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

const RefreshIcon = ({ spinning }: { spinning: boolean }) => (
  <CalendarClock className={`mr-1 h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
);

export const MyDayDashboard: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<MyDayResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showTask, setShowTask] = useState<boolean>(false);
  const [showFollowUp, setShowFollowUp] = useState<boolean>(false);
  const [showEvent, setShowEvent] = useState<boolean>(false);
  const [approvals, setApprovals] = useState<ApprovalSummary>(emptyApprovals);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [complaintActions, setComplaintActions] = useState<ComplaintAction[]>([]);

  const role = useEffectiveRole();
  const canReview = canAtLeast(role, "manager_trainer");
  const canComplaintActions = canAtLeast(role, "representative");
  const canMutate = user?.tenant_role !== "viewer";

  const refetch = useCallback(async () => {
    setError("");
    try {
      const r = await api.get("/crm/tasks/my-day");
      setData(r.data);
    } catch (e: any) {
      setError("Could not load My Day.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const loadApprovals = useCallback(async () => {
    if (!canReview) return;
    setApprovalsLoading(true);
    const [visitsResult, commissionsResult, claimsResult] = await Promise.allSettled([
      api.get("/crm/events", { params: { limit: 500 } }),
      api.get("/tenant/commission-center", { params: { status: "pending", limit: 100 } }),
      api.get("/tenant/expense-claims", { params: { status: "submitted" } }),
    ]);
    const next: ApprovalSummary = structuredClone(emptyApprovals);
    if (visitsResult.status === "fulfilled") {
      const visits = (visitsResult.value.data.items || []).filter((item: any) =>
        item.status === "pending_verification" &&
        (item.event_type_code === "home_visit" || String(item.event_type_code || "").includes("visit"))
      );
      next.visits = {
        count: visits.length,
        oldest: oldestDate(visits.map((item: any) => item.completed_at || item.updated_at || item.start_at)),
      };
    }
    if (commissionsResult.status === "fulfilled") {
      const entries = commissionsResult.value.data.entries || [];
      next.commissions = {
        count: entries.length,
        amount: entries.reduce((sum: number, item: any) => sum + Number(item.rule_snapshot?.rate_amount || 0), 0),
        currency: entries[0]?.rule_snapshot?.currency || "CAD",
        oldest: oldestDate(entries.map((item: any) => item.converted_at || item.created_at)),
      };
    }
    if (claimsResult.status === "fulfilled") {
      const claims = claimsResult.value.data.items || [];
      next.claims = {
        count: claims.length,
        amount: claims.reduce((sum: number, item: any) => sum + Number(item.total_amount || 0), 0),
        currency: claims[0]?.currency || "CAD",
        oldest: oldestDate(claims.map((item: any) => item.submitted_at || item.created_at)),
      };
    }
    setApprovals(next);
    setApprovalsLoading(false);
  }, [canReview]);

  useEffect(() => { void loadApprovals(); }, [loadApprovals]);

  const loadComplaintActions = useCallback(async () => {
    if (!canComplaintActions) { setComplaintActions([]); return; }
    try {
      const { data } = await api.get("/complaint-cases/inbox/my-day");
      setComplaintActions(data.items || []);
    } catch {
      setComplaintActions([]);
    }
  }, [canComplaintActions]);

  useEffect(() => { void loadComplaintActions(); }, [loadComplaintActions]);

  const totalApprovals = useMemo(() =>
    approvals.visits.count + approvals.commissions.count + approvals.claims.count,
  [approvals]);

  return (
    <>
      <PageHeader
        title={`My Day${user?.name ? ` · ${user.name}` : ""}`}
        subtitle={data?.tenant_timezone
          ? `Times shown in ${data.tenant_timezone}`
          : "Your tasks and follow-ups"}
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {canMutate ? (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="myday-quick-actions">
            <Button onClick={() => setShowTask(true)} data-testid="myday-add-task-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Task
            </Button>
            <Button variant="outline" onClick={() => setShowFollowUp(true)}
              data-testid="myday-add-followup-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Follow-Up
            </Button>
            <Button variant="outline" onClick={() => setShowEvent(true)}
              data-testid="myday-add-event-btn">
              <CalendarPlus className="w-4 h-4 mr-1" /> Schedule Appointment
            </Button>
            <Button variant="ghost" asChild data-testid="myday-open-calendar-btn">
              <a href="/calendar"><CalendarRange className="w-4 h-4 mr-1" /> Open Calendar</a>
            </Button>
          </div>
        ) : (
          <div className="text-sm text-slate-500 mb-6" data-testid="myday-read-only-note">
            Read-only view.
          </div>
        )}

        {canReview && (
          <section className="mb-6 rounded-lg border border-amber-200 bg-amber-50/50 p-4 md:p-5"
            data-testid="myday-approval-inbox">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Approvals requiring attention — {totalApprovals}</h2>
                <p className="mt-1 text-xs text-slate-600">One view of every pending decision; open the source workspace to review the full evidence.</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadApprovals} disabled={approvalsLoading}>
                <RefreshIcon spinning={approvalsLoading} /> Refresh approvals
              </Button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                { label: "Visit verifications", icon: ClipboardCheck, count: approvals.visits.count, detail: waitingLabel(approvals.visits.oldest), to: "/visit-verification" },
                { label: "Commission approvals", icon: BadgeDollarSign, count: approvals.commissions.count, detail: `${money(approvals.commissions.amount, approvals.commissions.currency)} · ${waitingLabel(approvals.commissions.oldest)}`, to: "/admin/commissions" },
                { label: "Claim approvals", icon: Receipt, count: approvals.claims.count, detail: `${money(approvals.claims.amount, approvals.claims.currency)} · ${waitingLabel(approvals.claims.oldest)}`, to: "/expenses" },
              ].map(({ label, icon: Icon, count, detail, to }) => (
                <Link key={label} to={to} className="rounded-lg border border-slate-200 bg-white p-4 transition hover:border-slate-400 hover:shadow-sm">
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-slate-600" />
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${count ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>{count}</span>
                  </div>
                  <div className="mt-3 text-sm font-semibold text-slate-900">{label}</div>
                  <div className="mt-1 min-h-8 text-xs text-slate-500">{detail}</div>
                  <div className="mt-3 flex items-center text-xs font-medium text-slate-700">Review now <ArrowRight className="ml-1 h-3.5 w-3.5" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {complaintActions.length > 0 && (
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4 md:p-5" data-testid="myday-complaint-actions">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Employee relations actions — {complaintActions.length}</h2>
                <p className="mt-1 text-xs text-slate-600">
                  {role === "owner_admin" || role === "admin_support"
                    ? "Restricted complaint cases or submitted IRs needing administrative attention."
                    : "Private Incident Report requests assigned to you."}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={loadComplaintActions}>Refresh</Button>
            </div>
            <div className="mt-4 space-y-2">
              {complaintActions.map((item) => (
                <Link key={item.id} to={item.href} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3 transition hover:border-slate-400">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{item.title}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {item.case_number || "Confidential case"} · {(item.status || "open").replace(/_/g, " ")}
                      {item.due_at ? " · Due " + new Date(item.due_at).toLocaleString() : ""}
                    </div>
                  </div>
                  <span className="inline-flex items-center text-xs font-medium text-slate-700">Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="text-sm text-slate-500" role="status" aria-busy="true"
            data-testid="myday-loading">Loading your day…</div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600" data-testid="myday-error">
            {error} <Button variant="outline" size="sm" onClick={refetch}
              className="ml-2" data-testid="myday-retry-btn">Retry</Button>
          </div>
        ) : data ? (
          <>
            <div className="text-xs text-slate-500 mb-3"
                 data-testid="myday-timezone-annotation">
              Times shown in <span className="font-medium">{data.tenant_timezone}</span> (tenant business timezone).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Today's appointments" icon={<CalendarPlus className="w-5 h-5" />} testId="todays-appointments">
              <EventList events={data.todays_appointments || []} onRefetch={refetch}
                testId="event-list-todays-appointments" readOnly={!canMutate}
                emptyText="No appointments today." />
            </Section>
            <Section title="Due today" icon={<CalendarClock className="w-5 h-5" />} testId="due-today">
              <TaskList tasks={data.due_today} onRefetch={refetch}
                testId="task-list-due-today" readOnly={!canMutate}
                emptyText="Nothing due today." />
            </Section>
            <Section title="Overdue follow-ups" icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
              testId="overdue-follow-ups">
              <TaskList tasks={data.overdue_follow_ups} onRefetch={refetch}
                testId="task-list-overdue-follow-ups" readOnly={!canMutate}
                emptyText="No overdue follow-ups. Great job." />
            </Section>
            <Section title="Upcoming (7 days)" icon={<ListChecks className="w-5 h-5" />} testId="upcoming">
              <TaskList tasks={data.upcoming} onRefetch={refetch}
                testId="task-list-upcoming" readOnly={!canMutate}
                emptyText="No upcoming tasks." />
            </Section>
            <Section title="Recently completed" icon={<CheckCircle2 className="w-5 h-5 text-green-700" />}
              testId="recent-completed">
              <TaskList tasks={data.recent_completed} onRefetch={refetch}
                testId="task-list-recent-completed" readOnly={true}
                emptyText="Nothing wrapped up in the last week yet." />
            </Section>
          </div>
          </>
        ) : null}
      </div>

      {canMutate && user ? (
        <TaskForm open={showTask} onOpenChange={setShowTask}
          onSuccess={() => refetch()}
          currentUserId={user.id} />
      ) : null}
      {canMutate && user ? (
        <TaskForm open={showFollowUp} onOpenChange={setShowFollowUp}
          onSuccess={() => refetch()}
          presetFollowUp
          currentUserId={user.id} />
      ) : null}
      {canMutate && user ? (
        <EventForm open={showEvent} onOpenChange={setShowEvent}
          onSuccess={() => refetch()}
          currentUserId={user.id} />
      ) : null}
    </>
  );
};
